import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { bringIntoBookApi } from '../../api/bringIntoBook';
import { bringIntoBookPath } from '../../utils/bringIntoBookPath';
import {
  BRING_SERIES_PEOPLE_LINK,
  BRING_SERIES_PEOPLE_NOT_NOW,
  SERIES_NOTES_CHIP_ARIA,
  SERIES_NOTES_CHIP_LONG,
  SERIES_NOTES_CHIP_SHORT,
  SERIES_NOTES_PICK,
  SERIES_NOTES_SHEET_TITLE,
  seriesNotesSheetLine,
} from '../../utils/writerCopy';
import './BringSeriesPeopleOffer.css';

// #2639: "Not now" is remembered per device and per book as the ids on offer at that moment.
const seenKey = (storyId) => `brainstormy.series-notes-chip.not-now.${storyId}`;

// The dot clears for good once the sheet is opened: the ids on offer then are stored the same way.
const openedKey = (storyId) => `brainstormy.series-notes-chip.opened.${storyId}`;

function readIds(key) {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(key));
    return Array.isArray(parsed) ? parsed : null;
  } catch (err) {
    return null;
  }
}

function writeIds(key, ids) {
  try {
    window.localStorage.setItem(key, JSON.stringify(ids));
  } catch (err) {
    // Private mode: the chip may come back next visit. Nothing is written.
  }
}

// What Bring into this book can carry (#900). Theme is a series idea, not a book note.
const BRINGABLE_KINDS = new Set(['people', 'world', 'custom']);

function bringableOffers(listing) {
  const offers = Array.isArray(listing?.offers) ? listing.offers : [];
  return offers.filter((offer) => BRINGABLE_KINDS.has(offer?.kind) && !offer.already_accepted);
}

/** True when the series still has notes this book has not brought in (run 21: a saved
 * Custom note, the series bible, was never offered because only people counted). */
export function hasSeriesPeopleToBring(listing) {
  return bringableOffers(listing).length > 0;
}

/**
 * Cold-start run 11: points a series book at Bring into this book (#900)
 * when the series has notes this book has not brought in. Free GET, writes
 * nothing. Only a known series book is asked (#1473). Standalone books,
 * and books whose series status is still unknown, render nothing and
 * make no request. Books with nothing left to bring render nothing.
 *
 * variant="card": a "Series notes" chip that opens a sheet (#2639).
 * variant="link": one line beside an empty-people message.
 */
export default function BringSeriesPeopleOffer({
  storyId,
  isSeries,
  variant = 'link',
  api = bringIntoBookApi,
}) {
  const [offers, setOffers] = useState([]);
  const [seenIds, setSeenIds] = useState(null);
  const [open, setOpen] = useState(false);
  const [openedIds, setOpenedIds] = useState(null);
  const chipRef = useRef(null);
  const sheetRef = useRef(null);
  const navigate = useNavigate();
  const isCard = variant === 'card';

  useEffect(() => {
    let cancelled = false;
    setOffers([]);
    setOpen(false);
    setSeenIds(isCard && storyId ? readIds(seenKey(storyId)) : null);
    setOpenedIds(isCard && storyId ? readIds(openedKey(storyId)) : null);
    if (!storyId || isSeries !== true) return undefined;
    api.list(storyId)
      .then((listing) => {
        if (!cancelled) setOffers(bringableOffers(listing));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [storyId, isSeries, isCard, api]);

  useEffect(() => {
    if (open) sheetRef.current?.querySelector('button')?.focus();
  }, [open]);

  if (offers.length === 0) return null;

  if (!isCard) {
    return (
      <p className="bring-series-people bring-series-people--line" data-testid="bring-series-people">
        <Link className="bring-series-people__link" to={bringIntoBookPath(storyId)}>
          {BRING_SERIES_PEOPLE_LINK}
        </Link>
      </p>
    );
  }

  if (seenIds && offers.every((offer) => seenIds.includes(offer.id))) return null;

  const names = offers.map((offer) => offer.display_name || offer.name);
  const count = offers.length;
  const showDot = !openedIds || offers.some((offer) => !openedIds.includes(offer.id));

  const close = () => {
    setOpen(false);
    chipRef.current?.focus();
  };

  const onSheetKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      close();
      return;
    }
    if (event.key !== 'Tab') return;
    const items = sheetRef.current.querySelectorAll('button');
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <>
      <button
        ref={chipRef}
        type="button"
        className="series-notes-chip"
        aria-label={SERIES_NOTES_CHIP_ARIA(count)}
        aria-haspopup="dialog"
        data-testid="bring-series-people"
        onClick={() => {
          const ids = offers.map((offer) => offer.id);
          writeIds(openedKey(storyId), ids);
          setOpenedIds(ids);
          setOpen(true);
        }}
      >
        {showDot && <span className="series-notes-chip__dot" aria-hidden="true" data-testid="series-notes-dot" />}
        <span className="series-notes-chip__long" aria-hidden="true">{SERIES_NOTES_CHIP_LONG(count)}</span>
        <span className="series-notes-chip__short" aria-hidden="true">{SERIES_NOTES_CHIP_SHORT(count)}</span>
      </button>
      {open && (
        <div className="series-notes-sheet__backdrop">
          <div
            ref={sheetRef}
            className="series-notes-sheet"
            role="dialog"
            aria-modal="true"
            aria-labelledby="series-notes-sheet-title"
            onKeyDown={onSheetKeyDown}
          >
            <h3 id="series-notes-sheet-title" className="series-notes-sheet__title">
              {SERIES_NOTES_SHEET_TITLE}
            </h3>
            <p className="series-notes-sheet__line">{seriesNotesSheetLine(names)}</p>
            <div className="bring-series-people__actions">
              <button
                type="button"
                className="series-notes-sheet__primary"
                onClick={() => navigate(bringIntoBookPath(storyId))}
              >
                {SERIES_NOTES_PICK}
              </button>
              <button
                type="button"
                className="bring-series-people__dismiss"
                onClick={() => {
                  const ids = offers.map((offer) => offer.id);
                  writeIds(seenKey(storyId), ids);
                  setSeenIds(ids);
                  setOpen(false);
                }}
              >
                {BRING_SERIES_PEOPLE_NOT_NOW}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
